// /awards — historical contract-award browser.
//
// Same EntityScreen primitive as /horizon, scoped to the AWARD entity.
// Default sort: award_date DESC (newest wins first). Per CLAUDE.md Iron
// Rule 11, this MUST be an EntityScreen — no fork.
//
// Distinct columns vs Horizon: vendor (the win recipient), award_date,
// total_contract_value (the headline number), contract_number.

import { ApiError, apiFetch } from '@/lib/api';
import { compactUsd, dateOnly, relTime } from '@/lib/format';
import { localizedField, useLocale } from '@/lib/locale';
import { useFilterRailLabels } from '@/lib/useFilterRailLabels';
import { EmptyState, Pill } from '@/primitives';
import { useAuth } from '@/state/auth';
import type { filters as f, search as s } from '@eidollon/contracts';
import { sortPinnedFirst, usePinned } from '@eidollon/intel-feed-client';
import { type ColumnDef, EntityScreen, PinStar, type SortState } from '@eidollon/ui';
import { SavedView, type SavedViewFetcher, decode, encode } from '@eidollon/ui-filters';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';

const awardsSearch = z.object({
  q: z.string().optional(),
  f: z.string().optional(),
  sort: z.string().optional(),
  cursor: z.string().optional(),
});
type AwardsSearch = z.infer<typeof awardsSearch>;

export const Route = createFileRoute('/_authed/awards')({
  validateSearch: awardsSearch,
  component: AwardsPage,
});

// Adapter from SavedView's fetcher contract → the terminal's auth-aware
// apiFetch. Module-scoped so the reference is stable across renders.
const savedViewFetcher: SavedViewFetcher = async ({ url, method, body }) =>
  apiFetch<unknown>(url, body !== undefined ? { method, body } : { method });

interface AwardRow {
  id: string;
  publication_date: string;
  jurisdiction: string;
  solicitation_number: string | null;
  contract_number: string | null;
  status: string;
  title: string | null;
  vendor_name: string | null;
  agency_raw_name: string | null;
  award_date: string | null;
  contract_value: number | null;
  total_contract_value: number | null;
  currency: string | null;
  procurement_category: string | null;
  procurement_method: string | null;
}

const awardsFilterSchema: f.FilterSchema = {
  entity: 'AWARD',
  defaultSort: { field: 'award_date', dir: 'desc' },
  fields: [
    {
      id: 'status',
      label: 'STATUS',
      type: 'multi-enum',
    },
    {
      id: 'jurisdiction',
      label: 'JURISDICTION',
      type: 'multi-enum',
      pinned: [
        { value: 'US', label: 'US' },
        { value: 'CA', label: 'CA' },
        { value: 'GB', label: 'GB' },
        { value: 'EU', label: 'EU' },
      ],
    },
    {
      id: 'agency',
      label: 'AGENCY',
      type: 'text',
    },
    {
      id: 'vendor',
      label: 'VENDOR',
      type: 'text',
    },
    {
      id: 'procurement_category',
      label: 'CATEGORY',
      type: 'multi-enum',
    },
    {
      id: 'procurement_method',
      label: 'METHOD',
      type: 'multi-enum',
    },
    {
      id: 'award_date',
      label: 'AWARDED',
      type: 'date-range',
    },
  ],
};

function AwardsPage() {
  const navigate = useNavigate({ from: '/awards' });
  const { locale } = useLocale();
  const filterRailLabels = useFilterRailLabels();
  const search = Route.useSearch() as AwardsSearch;

  // Tenant-scoped pinned awards. Separate IDB scope from opportunities
  // (entityKey='award' vs 'opp') — an award and an opp that happen to share
  // an id (they don't, but defence-in-depth) still get distinct pin state.
  const tenantId = useAuth((st) => st.me?.tenant_id ?? null);
  const pin = usePinned({ tenantId, entityKey: 'award' });

  const state: f.FilterState = useMemo(() => {
    if (!search.f) return {};
    try {
      return decode(search.f);
    } catch {
      return {};
    }
  }, [search.f]);

  const sort: SortState = useMemo(() => {
    const raw = search.sort ?? 'award_date:desc';
    const [field, dir] = raw.split(':');
    return { field: field || 'award_date', dir: dir === 'asc' ? 'asc' : 'desc' };
  }, [search.sort]);

  const setState = (next: f.FilterState) => {
    const enc = encode(next);
    const fParam = enc.kind === 'inline' ? enc.value || undefined : undefined;
    navigate({ search: (prev) => ({ ...prev, f: fParam, cursor: undefined }) });
  };
  const setSort = (next: SortState) => {
    navigate({
      search: (prev) => ({ ...prev, sort: `${next.field}:${next.dir}`, cursor: undefined }),
    });
  };

  const searchBody: s.SearchRequest = useMemo(
    () => ({
      ...(search.q ? { q: search.q } : {}),
      state,
      sort,
      limit: 100,
      personalize: false,
      ...(locale && locale !== 'en' ? { target_lang: locale } : {}),
    }),
    [search.q, state, sort, locale],
  );

  const awards = useQuery({
    queryKey: ['search', 'awards', searchBody],
    queryFn: async () =>
      apiFetch<{ rows: AwardRow[]; meta: s.SearchResponseMeta }>('/v1/search/awards', {
        method: 'POST',
        body: searchBody,
      }),
    placeholderData: (prev) => prev,
  });

  const aggBody: s.AggregationsRequest = useMemo(
    () => ({
      ...(search.q ? { q: search.q } : {}),
      state,
      fields: ['status', 'jurisdiction', 'procurement_category', 'procurement_method'],
    }),
    [search.q, state],
  );

  const aggs = useQuery({
    queryKey: ['search', 'awards', 'aggs', aggBody],
    queryFn: async () =>
      apiFetch<s.AggregationsResponse>('/v1/search/awards/aggregations', {
        method: 'POST',
        body: aggBody,
      }),
    staleTime: 30_000,
  });

  const facetCounts = useMemo(() => {
    const out: Record<string, { value: string; label?: string; count: number }[]> = {};
    for (const [field, agg] of Object.entries(aggs.data?.fields ?? {})) {
      out[field] = agg.buckets.map((b) => ({ value: b.value, count: b.count }));
    }
    return out;
  }, [aggs.data]);

  const columns: ColumnDef<AwardRow>[] = useMemo(
    () => [
      {
        id: 'pin',
        label: '',
        width: 24,
        sticky: 'left',
        align: 'center',
        render: (r) => (
          <PinStar
            pinned={pin.pinnedSet.has(r.id)}
            onToggle={() => pin.toggle(r.id)}
            label={pin.pinnedSet.has(r.id) ? 'unpin award' : 'pin award'}
          />
        ),
      },
      {
        id: 'id',
        label: 'ID',
        width: 96,
        render: (r) => (
          <span style={{ color: 'var(--cyan, #2eb8c7)', fontFamily: 'var(--mono)' }}>
            {r.contract_number ?? r.solicitation_number ?? r.id.slice(0, 8)}
          </span>
        ),
      },
      {
        id: 'jx',
        label: 'JX',
        width: 36,
        render: (r) => r.jurisdiction,
      },
      {
        id: 'agency',
        label: 'AGENCY',
        width: 200,
        render: (r) =>
          localizedField(r as unknown as Record<string, unknown>, 'agency_raw_name', locale) ??
          r.agency_raw_name ??
          '—',
      },
      {
        id: 'title',
        label: 'TITLE',
        width: 'flex',
        render: (r) => {
          const t = localizedField(r as unknown as Record<string, unknown>, 'title', locale);
          return t ?? r.title ?? <span style={{ color: 'var(--fg-3)' }}>—</span>;
        },
      },
      {
        id: 'vendor',
        label: 'VENDOR',
        width: 180,
        render: (r) => r.vendor_name ?? '—',
      },
      {
        id: 'value',
        label: '$ TOTAL',
        width: 90,
        align: 'right',
        render: (r) => compactUsd(r.total_contract_value ?? r.contract_value),
      },
      {
        id: 'awarded',
        label: 'AWARDED',
        width: 144,
        align: 'right',
        sortable: true,
        sortField: 'award_date',
        // Date + "Nd ago" pill — Sabre W3-B. Both helpers live in lib/format.
        render: (r) =>
          r.award_date ? (
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline' }}>
              <span>{dateOnly(r.award_date)}</span>
              <span className="mono dim" style={{ fontSize: '0.85em' }}>
                {relTime(r.award_date)}
              </span>
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'stat',
        label: 'STAT',
        width: 60,
        render: (r) => <Pill tone="gray">{(r.status ?? '—').slice(0, 3)}</Pill>,
      },
    ],
    // Pin column reads from `pin.pinnedSet` + `pin.toggle`.
    [pin.pinnedSet, pin.toggle, locale],
  );

  const rawRows = awards.data?.rows ?? [];
  // Pinned awards always at the top, in pin-order (newest first).
  const rows = useMemo(
    () => sortPinnedFirst(rawRows, pin.pinnedIds, (r) => r.id),
    [rawRows, pin.pinnedIds],
  );

  return (
    <EntityScreen<AwardRow>
      {...filterRailLabels}
      schema={awardsFilterSchema}
      state={state}
      onStateChange={setState}
      sort={sort}
      onSortChange={setSort}
      rows={rows}
      rowKey={(r) => r.id}
      columns={columns}
      onRowClick={(r) => navigate({ to: '/brief/$id', params: { id: r.id } })}
      count={rows.length}
      total={awards.data?.meta.total ?? null}
      loading={awards.isFetching}
      hasMore={Boolean(awards.data?.meta.cursor)}
      headerRightSlot={
        <SavedView entity="AWARD" filters={state} onLoad={setState} fetcher={savedViewFetcher} />
      }
      // No AI rail on awards (historical lookups; nothing to "rank for you").
      // The empty rail is the right call — better than fake-cold-start text.
      ai={null}
      facetCounts={facetCounts}
      emptyState={
        <EmptyState
          glyph="OPP"
          reason={awards.error ? 'LOAD FAILED' : 'NO AWARDS MATCH'}
          detail={
            awards.error
              ? `the search backend returned an error · id ${awards.error instanceof ApiError && awards.error.requestId ? awards.error.requestId : 'unknown'}`
              : 'try removing a filter, broadening the date range, or changing vendor / agency.'
          }
          actions={
            <button type="button" className="btn xs gh" onClick={() => setState({})}>
              CLEAR FILTERS
            </button>
          }
        />
      }
      meta={{
        backend: awards.data?.meta.backend ?? 'postgres-fts',
        latencyMs: awards.data?.meta.latencyMs,
        fromCache: awards.data?.meta.fromCache ?? false,
        sortLabel: `${sort.field}:${sort.dir}`,
        rightSlot:
          pin.count > 0 ? (
            <span
              className="mono"
              style={{
                fontSize: 10,
                color: 'var(--amber)',
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
              title={`${pin.count} pinned ${pin.count === 1 ? 'award' : 'awards'}`}
            >
              {`★ ${pin.count} PINNED`}
            </span>
          ) : null,
      }}
    />
  );
}
